import { Popup } from '@approvalmax/ui';
import { DatePicker, Form, Grid, Select, Spacing, Text } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TwoFaActionWizard } from 'modules/components';
import { domain } from 'modules/data';
import moment from 'moment';
import { memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
    useClearOnClose,
    useDateTimeLocalString,
    useFilteredTimeOptions,
    useGetAllowedDates,
    useGetDefaultDateTime,
    useHandlePopupButtonClick,
    useOpenPopup,
    useProcessAirwallexSchedulePayment,
} from './SchedulePaymentPopup.hooks';
import { messages } from './SchedulePaymentPopup.messages';
import { schema } from './SchedulePaymentPopup.schema';
import { Content } from './SchedulePaymentPopup.styles';
import { SchedulePaymentPopupProps, Step } from './SchedulePaymentPopup.types';

const SchedulePaymentPopup = memo<SchedulePaymentPopupProps>((props) => {
    const { request } = props;

    const [currentStep, setCurrentStep] = useState<Step>(Step.Schedule);
    const { isOpen, toggleOpen } = useOpenPopup();

    const scheduledPaymentDate =
        request.integrationCode === domain.IntegrationCode.XeroAirwallexBatchPayment
            ? request.details.scheduledPaymentDate
            : undefined;

    const { minDate, maxDate } = useGetAllowedDates();
    const { defaultDate, defaultSelectedHours } = useGetDefaultDateTime(minDate, scheduledPaymentDate);

    const { processSchedulePaymentApiCall, isLoading } = useProcessAirwallexSchedulePayment();

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        mode: 'onChange',
        defaultValues: {
            date: defaultDate,
            time: defaultSelectedHours?.toString() || '',
        },
    });

    const { watch } = form;

    const selectedDate = watch('date');
    const selectedTime = watch('time');

    const parsedSelectedTime = selectedTime ? parseInt(selectedTime, 10) : undefined;

    const filteredTimeOptions = useFilteredTimeOptions(minDate, maxDate, selectedDate);

    const dateTimeLocalString = useDateTimeLocalString(selectedDate, parsedSelectedTime);

    useEffect(() => {
        if (
            moment(selectedDate).isSame(moment(), 'day') &&
            parsedSelectedTime &&
            parsedSelectedTime < +filteredTimeOptions[0].id
        ) {
            form.setValue('time', filteredTimeOptions[0].id);
        }

        if (
            moment(selectedDate).isSame(maxDate, 'day') &&
            parsedSelectedTime &&
            parsedSelectedTime > +filteredTimeOptions[filteredTimeOptions.length - 1].id
        ) {
            form.setValue('time', filteredTimeOptions[filteredTimeOptions.length - 1].id);
        }
    }, [selectedDate, parsedSelectedTime, filteredTimeOptions, form, maxDate]);

    const handlePopupButtonClick = useHandlePopupButtonClick(
        currentStep,
        request.companyId,
        request.id,
        selectedDate,
        parsedSelectedTime,
        toggleOpen,
        setCurrentStep,
        processSchedulePaymentApiCall
    );

    useClearOnClose(isOpen, setCurrentStep);

    const onRequestClose = useCallback(() => toggleOpen(false), [toggleOpen]);

    if (currentStep === Step.Schedule) {
        return (
            <Popup isOpen={isOpen} onRequestClose={onRequestClose} disableAutoClose>
                <Popup.DefaultContent
                    title={currentStep === Step.Schedule ? messages.titleSchedule : messages.titleTwoFA}
                    buttons={
                        <Popup.DefaultContent.Button
                            disabled={isLoading || !form.formState.isValid}
                            execute={handlePopupButtonClick}
                        >
                            {scheduledPaymentDate ? messages.save : messages.create}
                        </Popup.DefaultContent.Button>
                    }
                >
                    <Content>
                        {currentStep === Step.Schedule && (
                            <Form form={form}>
                                <Spacing height={8} />

                                <Text font='body'>{messages.description}</Text>

                                <Spacing height={8} />

                                <Grid gap={32} gridTemplateColumns='repeat(2, 1fr)'>
                                    <DatePicker
                                        name='date'
                                        label={messages.paymentDate}
                                        placeholder={messages.placeholder}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                    />

                                    <Select
                                        name='time'
                                        label={messages.paymentTime}
                                        items={filteredTimeOptions}
                                        placeholder={messages.placeholder}
                                    />
                                </Grid>
                            </Form>
                        )}
                    </Content>
                </Popup.DefaultContent>
            </Popup>
        );
    }

    return (
        <TwoFaActionWizard
            title={messages.titleTwoFA}
            description={messages.twoFAToConfirm({ date: dateTimeLocalString, br: <br /> })}
            open
            onEnterAppCode={handlePopupButtonClick}
            onClose={toggleOpen}
            disableAutoClose
        />
    );
});

SchedulePaymentPopup.displayName = 'SchedulePaymentPopup';

export default SchedulePaymentPopup;
